import type { EvalJudgment } from "contracts/api/evals/comparisons";
import { EVAL_JUDGE_KIND } from "contracts/enums/evals";
import { For, Show } from "solid-js";
import { EVAL_JUDGE_KIND_COPY } from "../../enums/evals";
import { formatDate } from "../../ui/format";
import { PanelSection } from "../playground/PanelSection";
import {
  judgmentOfKind,
  standingVerdict,
  type VariantLabels,
  winnerLabel,
} from "./comparisonState";

/**
 * What has been decided about this comparison. The standing verdict leads,
 * because it is the answer; the model's reasoning sits under it, still readable
 * after a human has overruled it.
 */
export function EvalJudgmentPanel(props: {
  judgments: EvalJudgment[];
  labels: VariantLabels;
}) {
  const standing = () => standingVerdict(props.judgments);
  const llm = () => judgmentOfKind(props.judgments, EVAL_JUDGE_KIND.LLM);

  return (
    <>
      <PanelSection title="Verdict">
        <Show
          when={standing()}
          fallback={
            <span class="landing-sub">
              Nothing has been judged yet — the judge runs once both variants
              finish.
            </span>
          }
        >
          {(verdict) => (
            <>
              <p>
                <span class="badge badge-status st-done">
                  {winnerLabel(verdict().winner, props.labels)}
                </span>{" "}
                {EVAL_JUDGE_KIND_COPY[verdict().standing.judgeKind]},{" "}
                {formatDate(verdict().standing.createdAt)}
              </p>
              <Show when={verdict().standing.rationale}>
                {(rationale) => <p>{rationale()}</p>}
              </Show>
              <Show when={verdict().overridden}>
                {(overridden) => (
                  <p class="landing-sub">
                    Overrides the model, which chose{" "}
                    {winnerLabel(overridden().winner, props.labels)}.
                  </p>
                )}
              </Show>
            </>
          )}
        </Show>
      </PanelSection>

      <Show when={llm()}>
        {(judgment) => (
          <LlmJudgmentDetail judgment={judgment()} labels={props.labels} />
        )}
      </Show>
    </>
  );
}

function LlmJudgmentDetail(props: {
  judgment: EvalJudgment;
  labels: VariantLabels;
}) {
  return (
    <>
      <PanelSection title="Model reasoning">
        <p class="landing-sub mono">{props.judgment.judgeModel}</p>
        <Show when={props.judgment.rationale}>
          {(rationale) => <p>{rationale()}</p>}
        </Show>
        <Show when={props.judgment.passes}>
          {(passes) => (
            <p class="landing-sub">
              Position-bias control:{" "}
              <For each={passes()}>
                {(pass) => (
                  <span class="badge mono">
                    {pass.order} → {winnerLabel(pass.winner, props.labels)}
                  </span>
                )}
              </For>
            </p>
          )}
        </Show>
      </PanelSection>

      <Show when={props.judgment.perMetric.length > 0}>
        <PanelSection
          title="Per metric"
          count={props.judgment.perMetric.length}
          flush
        >
          <table class="tbl">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Winner</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              <For each={props.judgment.perMetric}>
                {(row) => (
                  <tr>
                    <td class="mono">{row.metricKey}</td>
                    <td>{winnerLabel(row.winner, props.labels)}</td>
                    <td>{row.note}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </PanelSection>
      </Show>
    </>
  );
}
